'use client';
import { useEffect, useState, useMemo, useContext } from 'react';
import { FaArrowRight, FaCheck } from 'react-icons/fa';
import Link from 'next/link';

import { BzImage } from '@benzinga/image';
import { UserManager } from '@benzinga/user-manager';
import { TrackingManager } from '@benzinga/tracking-manager';
import { handlePaywallLoginRedirect } from '@benzinga/auth-ui';
import { useIsUserLoggedIn, usePermission } from '@benzinga/user-context';
import { checkDeviceType } from '@benzinga/device-utils';
import { SessionContext } from '@benzinga/session-context';

export const ReportPaywall = ({ ticker }: { ticker: string }) => {
  const [showPaywall, setShowPaywall] = useState(false);
  const [showRedirectPaywall, setShowRedirectPaywall] = useState(false);
  const [showUnlockBanner, setShowUnlockBanner] = useState(false);
  const [claimedTicker, setClaimedTicker] = useState<string | null>(null);
  const isLoggedIn = useIsUserLoggedIn();
  const hasPermission = usePermission('com/reports', '#');
  const isBot = useMemo(() => checkDeviceType().isBot(), []);
  const session = useContext(SessionContext);
  const paywallId = `stock-report-paywall-${ticker}`;
  const paywallCampaign = 'stockreportpaywall';

  const baseCheckoutUrl = 'https://www.benzinga.com/premium/ideas/benzinga-edge';
  const urlParams = new URLSearchParams({
    utm_ad: paywallId,
    utm_adType: 'paywall',
    utm_campaign: paywallCampaign,
  });
  const checkoutUrl = `${baseCheckoutUrl}?${urlParams.toString()}`;

  useEffect(() => {
    const getFreeReportTicker = async () => {
      const userManager = session.getManager(UserManager);
      const reportTicker = await userManager.getGlobalSettings('stock_report_ticker');
      return reportTicker.ok as string | null;
    };

    if (isLoggedIn && showPaywall) {
      getFreeReportTicker().then((viewedTicker: string | null) => {
        setClaimedTicker(viewedTicker);
        if (viewedTicker && viewedTicker === ticker) {
          setShowUnlockBanner(true);
        } else if (viewedTicker && viewedTicker !== ticker) {
          setShowRedirectPaywall(true);
        }
      });
    }
  }, [isLoggedIn, showPaywall, ticker, session]);

  useEffect(() => {
    if (!isBot) {
      setShowPaywall(!hasPermission);
      if (!hasPermission) {
        session.getManager(TrackingManager).trackPaywallEvent('view', {
          paywall_id: paywallId,
          paywall_type: 'hard',
          placement: 'stock-report',
          utmParams: {
            ad: paywallId,
            adType: 'paywall',
            campaign: paywallCampaign,
          },
        });
      }
    }
  }, [hasPermission, isBot, paywallId, paywallCampaign, session]);

  const points = [
    'All Premium Content & Stories',
    'Real-Time Market Updates',
    'Exclusive Expert Insights',
    'Weekly Hidden Gem Stock Picks',
    'Professional-Level Research Tools',
  ];

  const handleRegisterRedirect = () => handlePaywallLoginRedirect(paywallId, 'register');
  const handleLoginRedirect = () => handlePaywallLoginRedirect(paywallId);

  const claimReport = async () => {
    const userManager = session.getManager(UserManager);
    await userManager.setGlobalSetting('stock_report_ticker', ticker);
    setShowUnlockBanner(true);
    session.getManager(TrackingManager).trackPaywallEvent('click', {
      paywall_id: paywallId,
      paywall_type: 'hard',
      placement: 'stock-report',
      utmParams: {
        ad: paywallId,
        adType: 'paywall',
        campaign: paywallCampaign,
      },
    });
  };

  if (showPaywall) {
    if (showUnlockBanner) {
      return (
        <Link
          href={checkoutUrl}
          className="fixed left-0 bottom-0 w-full bg-bzblue-800 text-white text-center flex items-center justify-center font-bold py-2"
        >
          <div className="flex flex-col md:flex-row items-center gap-2 text-sm md:text-base">
            <span className="stock-report-paywall_unlocked-report-text">
              Unlock All Stock Reports, Premium Content, and More Research Tools with
            </span>
            <BzImage height="20px" src="/next-assets/images/bz-edge-logo.svg" width="140px" />
          </div>
        </Link>
      );
    } else {
      return (
        <>
          <div className="absolute top-0 h-full w-full left-0" style={{ backdropFilter: 'blur(8px)' }}></div>
          <div className="fixed bottom-0 left-0 w-full flex items-center justify-center flex-col md:flex-row overflow-hidden bg-[#0B2040] z-50">
            <div className="w-full lg:w-fit">
              <div className="min-h-[232px] lg:min-w-[300px] pr-8 items-center">
                <div className="stock-report-paywall_logo-container flex flex-col md:flex-row items-start md:items-center p-4 pb-0">
                  <div className="w-32 mr-1">
                    <BzImage height="19px" src="/next-assets/images/bz-edge-logo-dark-v1.svg" width="170px" />
                  </div>
                </div>
                <div className="px-4 md:py-4">
                  <span className="stock-report-paywall_subhead text-blue-400 tracking-[0.1em] font-bold text-sm">
                    WHEN THE MARKET MOVES
                  </span>
                  <div className="stock-report-paywall_headline text-2xl md:text-3xl py-2 font-extrabold text-white">
                    Edge Members Already <br /> Know What to Do
                  </div>
                  <span className="stock-report-paywall_save-text text-blue-300 text-sm font-bold">
                    Save 43% when you Join Today
                  </span>
                  {claimedTicker && (
                    <div className="stock-report-paywall_claimed-text text-blue-300 text-xs bg-white/10 px-2 py-1 rounded-full w-fit mt-2">
                      You have already claimed {claimedTicker} Stock Report
                    </div>
                  )}
                </div>
                {!isLoggedIn && (
                  <div
                    className="stock-report-paywall_login-text w-full px-4 py-2 text-[#95aed1] text-xs z-1 border-none md:border-l border-slategray flex flex-row items-center gap-4 cursor-pointer"
                    onClick={handleLoginRedirect}
                  >
                    Already a member?
                    <div className="text-[#b2c7e6] flex flex-row items-center justify-start gap-1 uppercase font-bold">
                      Login <FaArrowRight className="text-[#225AA9] text-xs" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 md:p-8 relative w-fit flex flex-col items-start justify-center">
              <div className="w-full hidden md:flex flex-col items-center justify-center mb-4">
                <div className="stock-report-paywall_members-button bg-[#225AA9] px-4 py-2 text-sm font-extrabold text-white w-fit uppercase tracking-[0.1em]">
                  members receive
                </div>
              </div>
              <div className="bg-white p-4 rounded-sm relative w-fit">
                <div className="flex flex-col items-center justify-center gap-4">
                  <ul className="stock-report-paywall_list-container list-none pb-2 text-sm md:text-base w-fit whitespace-nowrap">
                    {points.map((item: string, index: number) => {
                      return (
                        <li
                          className="stock-report-paywall_list-item flex items-center gap-2 text-[#192940] whitespace-nowrap md:mb-1"
                          key={index}
                        >
                          <FaCheck className="text-bzblue-700" />
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="w-full">
                  <Link href={checkoutUrl} target="_blank">
                    <div className="stock-report-paywall_unlock-access-text bg-[#225AA9] px-4 py-2 text-white text-xs md:text-sm font-bold uppercase text-center w-full mb-2">
                      unlock access now
                    </div>
                  </Link>
                  {!isLoggedIn ? (
                    <button onClick={handleRegisterRedirect}>
                      <div className="stock-report-paywall_signup-text bg-white px-4 py-2 text-[#225AA9] border border-[#225AA9] text-xs md:text-sm font-bold uppercase text-center w-full">
                        Sign up to claim your free report
                      </div>
                    </button>
                  ) : showRedirectPaywall && claimedTicker ? (
                    <Link href={`/quote/${claimedTicker}/report`}>
                      <div className="stock-report-paywall_view-report-text bg-white px-4 py-2 text-[#225AA9] border border-[#225AA9] text-xs md:text-sm font-bold uppercase text-center w-full">
                        View Your Claimed Report
                      </div>
                    </Link>
                  ) : (
                    <button
                      className="stock-report-paywall_claim-report-text bg-white px-4 py-2 text-[#225AA9] border border-[#225AA9] text-xs md:text-sm font-bold uppercase text-center w-full"
                      onClick={claimReport}
                    >
                      Claim {ticker} Stock Report
                    </button>
                  )}
                  {isLoggedIn && !claimedTicker && (
                    <Link
                      href={'/reports'}
                      className="stock-report-paywall_claim-different-report-text text-blue-800 text-sm text-center w-full flex flex-row items-center justify-center gap-1 mt-1"
                    >
                      Claim a different report <FaArrowRight className="text-[#225AA9] text-xs" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }
  } else {
    return null;
  }
};

export default ReportPaywall;
